import React, { useEffect, useRef, useState } from "react";
import ShutDown from "./ShutDown";
import { useOpenPage, useWindowManager } from "./WindowManager";

const PROGRAMS = [
  { label: "Minesweeper", icon: "/desktop-icons/minesweeper-icon.png", app: "minesweeper" },
  { label: "MSN", icon: "/desktop-icons/msn-icon.png", page: "/contact" },
  { label: "Documents", icon: "/desktop-icons/documents-icon.png", app: "documents" },
  { label: "Recycle Bin", icon: "/desktop-icons/recycle-bin-icon.png", app: "recycleBin" },
];

const DOCUMENTS = [
  { label: "About Me", icon: "/desktop-icons/document-icon.png", page: "/about" },
  { label: "Projects", icon: "/desktop-icons/document-icon.png", page: "/projects" },
  { label: "Reading", icon: "/desktop-icons/document-icon.png", page: "/reading" },
  { label: "Contact", icon: "/desktop-icons/document-icon.png", page: "/contact" },
];

const RAISED = "bg-[#c0c0c0] border-[0.13rem] border-t-[#dfdfdf] border-l-[#dfdfdf] border-b-black border-r-black";

const Arrow = () => (
  <svg width="4" height="7" viewBox="0 0 4 7" aria-hidden="true" className="ml-auto shrink-0">
    <path d="M0 0 L4 3.5 L0 7 Z" fill="currentColor" />
  </svg>
);

const Row = ({ icon, iconClassName, label, large, hovered, onHover, onChoose, children }) => (
  <div
    className={`relative flex items-center gap-2 pl-1 pr-2 cursor-default ${large ? "h-10" : "h-7"} ${
      hovered ? "bg-[#000080] text-white" : ""
    }`}
    onPointerEnter={onHover}
    onClick={onChoose}
  >
    <img
      src={icon}
      alt=""
      className={`shrink-0 [image-rendering:pixelated] ${iconClassName ?? (large ? "w-8 h-8" : "w-5 h-5")}`}
      draggable={false}
    />
    <span className="mt-[0.2rem] whitespace-nowrap">{label}</span>
    {children && <Arrow />}
    {/* Submenus stay open while the pointer is anywhere inside the parent row. */}
    {hovered && children && (
      <div className={`absolute left-full top-[-0.2rem] min-w-[11rem] py-[0.1rem] text-black ${RAISED}`}>
        {children}
      </div>
    )}
  </div>
);

const SubMenu = ({ items, onChoose }) => {
  const [hovered, setHovered] = useState(null);
  return items.map((item) => (
    <Row
      key={item.label}
      icon={item.icon}
      label={item.label}
      hovered={hovered === item.label}
      onHover={() => setHovered(item.label)}
      onChoose={(e) => {
        e.stopPropagation();
        onChoose(item);
      }}
    />
  ));
};

const StartMenu = () => {
  const { activate } = useWindowManager();
  const openPage = useOpenPage();
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [shutDownStage, setShutDownStage] = useState(null);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e) => !rootRef.current.contains(e.target) && setOpen(false);
    const onKeyDown = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const toggle = () => {
    setHovered(null);
    setOpen((o) => !o);
  };

  const choose = (item) => {
    setOpen(false);
    if (item.app) activate(item.app);
    if (item.page) openPage(item.page);
  };

  return (
    <div ref={rootRef} className="relative mr-2 select-none">
      <button className="relative block h-10" aria-label="Start" aria-expanded={open} onClick={toggle}>
        <img
          src="/start-normal.png"
          alt=""
          className={`h-10 ${open ? "translate-x-px translate-y-px" : ""}`}
          draggable={false}
        />
        {/* The button art bakes in a raised bevel; while open, cover it with a sunken one. */}
        {open && (
          <span className="absolute inset-[3px] border-2 border-t-black border-l-black border-b-white border-r-white pointer-events-none" />
        )}
      </button>

      {open && (
        <div className={`absolute bottom-full left-0 mb-[0.15rem] flex desktop-font text-2xl text-black ${RAISED}`}>
          <div className="flex items-end w-8 bg-gradient-to-b from-[#000080] to-[#1084d0]">
            <span className="pt-2 [writing-mode:vertical-rl] rotate-180 text-white text-[1.9rem] leading-none whitespace-nowrap">
              <span className="font-bold">Aaron</span> 98
            </span>
          </div>

          <div className="flex flex-col min-w-[13rem] py-[0.1rem]">
            <Row
              large
              icon="/desktop-icons/documents-icon.png"
              label="Programs"
              hovered={hovered === "programs"}
              onHover={() => setHovered("programs")}
            >
              <SubMenu items={PROGRAMS} onChoose={choose} />
            </Row>
            <Row
              large
              icon="/desktop-icons/document-icon.png"
              label="Documents"
              hovered={hovered === "documents"}
              onHover={() => setHovered("documents")}
            >
              <SubMenu items={DOCUMENTS} onChoose={choose} />
            </Row>
            <Row
              large
              icon="/desktop-icons/help-book-icon.png"
              label="Help"
              hovered={hovered === "help"}
              onHover={() => setHovered("help")}
              onChoose={() => choose({ app: "notepad" })}
            />
            <div className="mx-1 my-[0.2rem] border-t border-t-[#808080] border-b border-b-white" />
            <Row
              large
              icon="/desktop-icons/shut-down-icon.png"
              // This icon is drawn on a padded 48px canvas; show it at native size within the 32px slot.
              iconClassName="w-12 h-12 -m-2"
              label="Shut Down..."
              hovered={hovered === "shutdown"}
              onHover={() => setHovered("shutdown")}
              onChoose={() => {
                setOpen(false);
                setShutDownStage("dialog");
              }}
            />
          </div>
        </div>
      )}

      <ShutDown stage={shutDownStage} setStage={setShutDownStage} />
    </div>
  );
};

export default StartMenu;
